import type {
  AccountAppearance,
  AuthIdentityView,
  AuthLoginMethodsView,
  AuthResultView,
  ExternalAuthFlowView,
  LegalAcceptanceInput,
  LegalAcceptanceDetailView,
  LegalDocumentDefinition,
  LegalDocumentKey,
  MarketingAttribution,
  PrivacyCenterView,
  PrivacyRequestType,
  PrivacyRequestView,
  PhoneChallengeView,
  UserView,
  WechatWebTransactionView,
} from 'shared';
import { http } from './client';
import { getDesktopBridge } from '@/desktop';
import { getMobileClient } from '@/services/mobile-client';

async function sessionPost<T>(path: string, input: unknown): Promise<T> {
  const mobile = getMobileClient();
  return mobile ? mobile.authenticate<T>(path, input) : http.post<T>(`/auth/${path}`, input);
}

export interface UpdateProfileInput {
  displayName?: string;
  avatar?: string | null;
  locale?: string;
  timezone?: string;
  developerModeEnabled?: boolean;
  pageSidebarPinned?: boolean;
  workspaceHomeMode?: 'chat' | 'workbench';
  workspaceOnboarding?: UserView['workspaceOnboarding'];
  workspaceSidebarCollapsed?: boolean;
  knowledgeAutoOrganizeFiles?: boolean;
  knowledgeAutoRecall?: boolean;
  appearance?: AccountAppearance;
}

export const authApi = {
  loginMethods(): Promise<AuthLoginMethodsView> {
    return http.get('/auth/login-methods');
  },
  async legalDocuments(): Promise<Record<LegalDocumentKey, LegalDocumentDefinition>> {
    const documents = await http.get<Record<LegalDocumentKey, LegalDocumentDefinition>>('/legal/documents');
    // 兼容已发布文档中保存的旧图标名。
    const information = documents['personal-information'];
    if (information?.icon === 'ri-list-check-3-line') information.icon = 'ri-list-check-3';
    return documents;
  },
  login(input: {
    email: string;
    password: string;
    captchaPayload?: string;
  }): Promise<AuthResultView> {
    const desktop = getDesktopBridge();
    if (desktop) return desktop.auth.login(input);
    return sessionPost('login', input);
  },
  requestPhoneChallenge(input: {
    phone: string;
    captchaPayload: string;
  }): Promise<PhoneChallengeView> {
    return http.post('/auth/phone/challenges', input);
  },
  loginWithPhone(input: { challengeId: string; code: string }): Promise<ExternalAuthFlowView> {
    return sessionPost('phone/login', input);
  },
  registerPendingIdentity(input: {
    pendingToken: string;
    displayName: string;
    referralCode?: string;
    legalAcceptance: LegalAcceptanceInput;
  }): Promise<AuthResultView> {
    return sessionPost('pending-identities/register', input);
  },
  bindPendingIdentity(input: {
    pendingToken: string;
    email: string;
    password: string;
    captchaPayload?: string;
  }): Promise<AuthResultView> {
    return sessionPost('pending-identities/bind', input);
  },
  beginWechatWeb(): Promise<WechatWebTransactionView> {
    return http.post('/auth/wechat-web/transactions');
  },
  exchangeWechatWeb(
    id: string,
    pollToken: string,
  ): Promise<AuthResultView | ExternalAuthFlowView | { status: 'pending' }> {
    return sessionPost(`wechat-web/transactions/${id}/exchange`, { pollToken });
  },
  register(input: {
    email: string;
    password: string;
    displayName: string;
    locale?: string;
    timezone?: string;
    referralCode?: string;
    marketing?: MarketingAttribution;
    captchaPayload: string;
    emailVerificationChallengeId?: string;
    emailVerificationCode?: string;
    legalAcceptance: LegalAcceptanceInput;
  }): Promise<AuthResultView> {
    return sessionPost('register', input);
  },
  me(): Promise<UserView> {
    return http.get('/auth/me');
  },
  updateMe(input: UpdateProfileInput): Promise<UserView> {
    return http.patch('/auth/me', input);
  },
  claimOnboardingQuestion(startedAt: string): Promise<{ claimed: boolean }> {
    return http.post('/auth/me/onboarding-question-claim', { startedAt });
  },
  listLoginMethods(): Promise<AuthIdentityView[]> {
    return http.get('/auth/me/login-methods');
  },
  requestRegistrationEmailVerification(input: {
    email: string;
    captchaPayload: string;
  }): Promise<PhoneChallengeView> {
    return http.post('/auth/email-verifications/register', input);
  },
  bindEmailPassword(input: {
    email: string;
    password: string;
    challengeId: string;
    code: string;
  }): Promise<UserView> {
    return http.post('/auth/me/login-methods/email', input);
  },
  requestEmailVerification(input?: { email?: string }): Promise<PhoneChallengeView> {
    return http.post('/auth/me/email-verifications', input ?? {});
  },
  confirmEmailVerification(input: { challengeId: string; code: string }): Promise<UserView> {
    return http.post('/auth/me/email-verifications/confirm', input);
  },
  requestPhoneBindChallenge(input: {
    phone: string;
    captchaPayload: string;
  }): Promise<PhoneChallengeView> {
    return http.post('/auth/me/login-methods/phone/challenges', input);
  },
  bindPhone(input: { challengeId: string; code: string }): Promise<void> {
    return http.post('/auth/me/login-methods/phone', input);
  },
  beginWechatWebBind(): Promise<WechatWebTransactionView> {
    return http.post('/auth/me/login-methods/wechat-web/transactions');
  },
  confirmWechatWebBind(id: string, pollToken: string): Promise<{ status: 'pending' | 'bound' }> {
    return http.post(`/auth/me/login-methods/wechat-web/transactions/${id}/confirm`, { pollToken });
  },
  removeLoginMethod(identityId: string): Promise<void> {
    return http.delete(`/auth/me/login-methods/${identityId}`);
  },
  updateCreativeCenter(enabled: boolean): Promise<UserView> {
    return http.patch('/auth/me/creative-center', { enabled });
  },
  uploadAvatar(image: File): Promise<UserView> {
    const form = new FormData();
    form.append('image', image);
    return http.post('/auth/me/avatar', form);
  },
  logout(): Promise<void> {
    if (getDesktopBridge()) return getDesktopBridge()!.auth.logout();
    return http.post('/auth/logout');
  },
  logoutAll(): Promise<void> {
    return http.post('/auth/logout-all');
  },
  changePassword(input: { currentPassword: string; newPassword: string }): Promise<void> {
    return http.post('/auth/change-password', input);
  },
  privacyCenter(): Promise<PrivacyCenterView> {
    return http.get('/auth/me/privacy');
  },
  privacyAcceptance(acceptanceId: string): Promise<LegalAcceptanceDetailView> {
    return http.get(`/auth/me/privacy-acceptances/${acceptanceId}`);
  },
  submitPrivacyRequest(input: {
    type: PrivacyRequestType;
    details: string;
  }): Promise<PrivacyRequestView> {
    return http.post('/auth/me/privacy-requests', input);
  },
};
